// Angular
import { Injectable } from "@angular/core";

// Third party
import * as _ from 'lodash';

// Local library
import { GUEST, IGetEventsDetailsResponse } from "@amwager/util-ts";
import { WagerFacade } from "../+state/wager.facade";
import {
  PermissionService,
  SessionService
} from "@amwager/data-access";
import { AccountFacade } from "@amwager/account/data-access";

/**
 * Race-level helpers — DBL/PK grid, runner padding, DOM leg cleanup
 */
@Injectable()
export class RaceService {

  events: any;
  selectedEvent: any;

  constructor(
    private readonly permissionService: PermissionService,
    private readonly accountFacade: AccountFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly sessionService: SessionService
  ) {}

  /**
   * Pad runners with replica/empty entries to align coupled-entry columns
   * @param {any[]} data Runners per race
   * @param {string} platform Platform hint (e.g. 'mobile')
   * @returns {any[]} Padded runners
   */
  processFillInRunners(data: any[], platform?: string): any[] {

    let previousRunners: any[] = [];

    const updatedData = data.map(item => {
      const currentRunners = _.cloneDeep(item.Runners);

      currentRunners.forEach((
        currentRunner: {
          RunnerId: any; Entries: {
            RunnerId?: number, EntryId: any; Name: string; isReplica: boolean;
          }[];
        }) => {

        if (
          currentRunner.RunnerId &&
          (
            !currentRunner.Entries ||
            currentRunner.Entries.length === 0 ||
            !currentRunner.Entries.some(entry => entry.Name)
          )
        ) {
          currentRunner.Entries = currentRunner.Entries || [];
          currentRunner.Entries.push({
            RunnerId: currentRunner.RunnerId,
            EntryId: null,
            Name: '',
            isReplica: false
          });
        }

        if (!_.isEmpty(previousRunners)) {

          const prevRunner = _.find(previousRunners, { RunnerId: currentRunner.RunnerId });

          if (prevRunner) {

            _.forEach(prevRunner.Entries, prevEntry => {
              if (prevEntry.EntryId !== undefined &&
                  !_.some(currentRunner.Entries, { EntryId: prevEntry.EntryId })) {
                currentRunner.Entries.push({
                  EntryId: prevEntry.EntryId,
                  Name: '',
                  isReplica: true
                });
              }
            });
          }
        }
      });

      previousRunners = _.cloneDeep(currentRunners);

      return {
        ...item,
        Runners: currentRunners
      };
    });

    const maxTotalEntries = _.max(
      _.map(updatedData, item =>
        _.sumBy(item.Runners, (runner: any) => runner.Entries.length)
      )
    ) || 0;

    const finalData = updatedData.map(item => {
      const extendedRunners = _.cloneDeep(item.Runners);
      const totalEntries = _.sumBy(extendedRunners, (runner: any) => runner.Entries.length);

      for (let i = totalEntries; i < maxTotalEntries; i++) {
        extendedRunners.push({
          RunnerId: extendedRunners.length + 1,
          Entries: [{ toHide: true }],
          Odds: '',
          toHide: true
        });
      }

      return {
        ...item,
        Runners: extendedRunners
      };
    });

    if (platform === 'mobile') {
      while (finalData.length < 5) {
        const newRunners = Array.from({ length: maxTotalEntries || 1 }, (_, i) => ({
          RunnerId: i + 1,
          Entries: [{ toHide: true }],
          Odds: '',
          toHide: true
        }));

        finalData.push({ RaceId: 0, Runners: newRunners });
      }
    }

    return finalData;
  }

  /**
   * Ensure each runner has at least one entry (non-DBL/PK case)
   * @param {any[]} data Runners
   * @returns {any[]} Runners with defaulted entries
   */
  processFillInRunnersNonDblPk(data: any[] = []): any[] {
    return _.map(data, item => {
      const clonedItem = { ...item };
      if (!clonedItem.Entries || clonedItem.Entries.length === 0) {
        clonedItem.Entries = [{
          RunnerId: clonedItem.RunnerId,
          EntryId: null,
          Name: '',
          isReplica: false
        }];
      }
      return clonedItem;
    });
  }

  /**
   * True when both previous and current pools are exotic and selections are enabled
   * @param {string} prevPoolId Previous pool id
   * @param {boolean} isExoticSelections Exotic-selections flag
   * @param {string[]} poolIds Ids that are considered exotic
   * @param {any} selectedPool Current pool
   * @returns {boolean} True when the pool remains exotic
   */
  checkExotic(
    prevPoolId: string,
    isExoticSelections: boolean,
    poolIds: string[],
    selectedPool: any
  ): boolean {
    if (
      isExoticSelections &&
      (
        poolIds.includes(selectedPool?.PoolId)
      ) &&
      (
        poolIds.includes(prevPoolId || '')
      )
    ) {
      return true;
    }
    else return false;
  }

  /**
   * Deactivate all leg selectors and restore the default "All" label on web
   * @param {any} elementRef Host ElementRef
   * @returns {void}
   */
  clearActiveAllLeg(elementRef: any): void {
    const root = elementRef?.nativeElement;
    if (!root) return;

    // .web-view mirrors headLegSelector so we only restore text on web (mobile shows an icon)
    const webView = root.querySelector('.web-view');

    const divLegs = root.querySelectorAll(`.mode-place-races span.mode`);
    divLegs.forEach((leg: HTMLElement) => {
      leg?.classList.remove('active');
      if (webView) {
        leg.innerText = 'All';
      }
    });
  }

  /**
   * Clear runner-cell selections in the current race grid
   * @param {any} elementRef Host ElementRef
   * @returns {void}
   */
  clearLegRoundSelector(elementRef: any): void {
    const divLegs = elementRef.nativeElement.querySelectorAll('.table-runner [class^="runner"]');
    // const divLegs = this.elementRef.nativeElement.querySelectorAll('.table-bet [class^="leg"]');
    divLegs.forEach((leg: HTMLElement) => {
      if (leg?.classList.contains('active'))
        leg?.classList.remove('active');
        leg?.classList.remove('couple-entry');
        leg.innerHTML = '';
    });
  }

  /**
   * Slice N consecutive races starting at raceId (for DBL/PK grids)
   * @param {any} races Event races
   * @param {number} raceId Starting race id
   * @param {number} pk Number of legs to include
   * @returns {any[]} Sliced races
   */
  storeDBLPKraces(races: any, raceId: number, pk: number): Array<any> {
    const startIndex = _.findIndex(races, { RaceId: raceId });
    const result = _.slice(races, startIndex, startIndex + pk);
    return result;
  }

  /**
   * Map a modifier label to its abbreviation (Box/KeyBox/PowerBox/KeyWheel)
   * @param {string} word Modifier label
   * @returns {string} Two-letter modifier id
   */
  convertWordsToAbbre(word: string): string {
    let modifier =  word;

    switch(modifier) {
      case 'Box':
          modifier = 'BX'
        break;
      case 'KeyBox':
          modifier = 'KB'
        break;
      case 'PowerBox':
          modifier = 'PB'
        break;
      case 'KeyWheel':
          modifier = 'KW'
        break;
      default:
          modifier = ''
        break;
    }

    return modifier;
  }

  /**
   * True when the race has any runner data
   * @param {any} selectedRace Current race
   * @returns {boolean} True when Runners exist and are non-empty
   */
  checkDataDBLPK(selectedRace: any): boolean {

    if (selectedRace.Runners === undefined)
      return false;

    if (!selectedRace.Runners.length)
      return false;

    return true;
  }

  /**
   * Parse the first integer out of a string (e.g. "PK4" -> 4)
   * @param {string} pk Source string
   * @returns {number} Parsed integer, or NaN
   */
  extractNumber(pk: string): number {
    const match = pk.match(/\d+/);
    return match ? parseInt(match[0], 10) : NaN;
  }

  /**
   * Smooth-scroll the pagination container so the given page is centered
   * @param {any} page 1-based page index
   * @param {any} swipeSec Pagination container ElementRef
   * @returns {void}
   */
  scrollToPage(page: any, swipeSec: any): void {
    if (swipeSec === undefined) return;
    const paginationItems = swipeSec.nativeElement.children;
    const activeItem = paginationItems[page - 1];

    if (activeItem) {
      const containerWidth = swipeSec.nativeElement.offsetWidth;
      const itemWidth = activeItem.offsetWidth;
      const itemLeft = activeItem.offsetLeft;
      const scrollPosition = itemLeft - containerWidth / 2 + itemWidth / 2;

      swipeSec.nativeElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });

    }
  }

  /**
   * Scroll to the active page and emit its race status to the parent
   * @param {any} page Active race object
   * @param {any} swipeSec Pagination container ElementRef
   * @param {any} doSetRaceStatusFn EventEmitter for race status
   * @returns {void}
   */
  setDefaultValues(page: any, swipeSec: any, doSetRaceStatusFn: any): void {

    // auto scroll view
    setTimeout(()=>{
      this.scrollToPage(page, swipeSec);
    }, 100);

    doSetRaceStatusFn.emit(page?.RaceStatus);
  }

  /**
   * Build the DBL/PK race grid for the selected pool (or a single race otherwise)
   * @param {any} foundObject Selected pool
   * @param {any} selectedEvent Active event
   * @param {any} selectedRace Active race
   * @param {string} platform Platform hint (e.g. 'mobile')
   * @returns {any} { dblPkArray, selectedRace }
   */
  fetchDblPkArrayAndNone(
    foundObject: any,
    selectedEvent: any,
    selectedRace: any,
    platform?: string
  ): any {

    let dblPkArray: any = [];

    // OLD (name allowlist skipped multi-race pools like QD/DQ/TT/TS/BQ/TR2/SP2):
    // const startsWithPK = /^PK/.test(foundObject.PoolId || '');
    // const wordWithPK = /^PICK/.test(foundObject.PoolName || '');

    // PoolRaces is the number of consecutive races the pool spans, so it drives
    // the R1..Rn grid for every multi-race pool (DBL, PK3..PK12, GSL, QD, ...).
    const poolRaces = foundObject?.PoolRaces || 1;
    const isMultiRace = poolRaces > 1;

    if (selectedEvent !== undefined && selectedRace !== undefined) {

      if (!this.checkDataDBLPK(selectedRace)) {
        return dblPkArray;
      }

      // OLD:
      // if (startsWithPK || wordWithPK) {
      //   if (
      //     this.extractNumber(foundObject.PoolId) &&
      //     this.extractNumber(foundObject.PoolId) !== Number(NaN)
      //   ) {
      //     dblPkArray = this.storeDBLPKraces(
      //       selectedEvent.ToteResponse.EventDetail.Races,
      //       selectedRace.RaceId as number,
      //       this.extractNumber(foundObject.PoolId)
      //     );
      //   }
      // }
      // else if (foundObject.PoolId === 'GSL') {
      //   dblPkArray = this.storeDBLPKraces(
      //     selectedEvent.ToteResponse.EventDetail.Races,
      //     selectedRace.RaceId as number,
      //     (4)
      //   );
      // }
      // else if (foundObject.PoolId === 'DBL') {
      //   dblPkArray = this.storeDBLPKraces(
      //     selectedEvent.ToteResponse.EventDetail.Races,
      //     selectedRace.RaceId as number,
      //     (2)
      //   );
      // } else {
      if (isMultiRace) {
        dblPkArray = this.storeDBLPKraces(
          selectedEvent.ToteResponse.EventDetail.Races,
          selectedRace.RaceId as number,
          poolRaces
        );
      } else {
        dblPkArray = '';
        const updatedRunners = this.processFillInRunnersNonDblPk(selectedRace.Runners);
        const updatedRace = _.cloneDeep(selectedRace);
        updatedRace.Runners = updatedRunners;
        selectedRace = updatedRace;
      }
    }

    // OLD:
    // if (
    //   foundObject.PoolId === 'GSL' ||
    //   foundObject.PoolId === 'DBL' ||
    //   startsWithPK || wordWithPK
    // ) {
    if (isMultiRace) {
      dblPkArray = this.processFillInRunners(dblPkArray, platform);
    }

    return {
      dblPkArray: dblPkArray,
      selectedRace: selectedRace
    };
  }
}
